import { z } from 'zod'

import { findSegmentCollisions } from './segment-dedup'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'
import {
  getCachedFilter,
  savedFilterStructureSchema,
} from '@/Atomic/Widgets/AiChatBot/core/tools/filter-normalizer'
import { requireDataClassName } from '@/Atomic/Widgets/AiChatBot/core/tools/shared/entity-serializer'

/**
 * Duplicate guard for segment creation (Flow B): detects whether a dynamic segment with
 * the SAME filter (or same name) already exists before a new one is created. Delegates
 * the scan to the shared findSegmentCollisions helper — the same one create_segment runs
 * at the write boundary — so there is one dedup implementation. On a hit the model offers
 * "open the existing segment" vs "create a duplicate anyway"; on none it proceeds.
 *
 * The proposed filter is read from the build_filter cache (getCachedFilter), not the
 * model-relayed `structure` arg, which drifts when carried across turns.
 */
export const findMatchingSegmentTool = defineTool({
  name: 'find_matching_segment',
  description:
    'Before creating a NEW segment, check whether the user already has a segment that collides — by filter OR by name. ' +
    'Call this in the segment-create flow AFTER the filter is confirmed (show_filter_preview save result) and BEFORE create_segment. ' +
    'Pass the base entityType (Company or Person), the exact structure from show_filter_preview, and the segment name the user intends. ' +
    'Returns two independent signals: `match` — a segment with the EXACT same filter already exists (tell the user by title and ask whether to open it or create a duplicate); `nameMatch` — a segment with the SAME name but a DIFFERENT filter exists (tell the user the name is taken and ask whether to pick another name, open the existing one, or create anyway). ' +
    'Either or both may be null. When both are null, proceed with create_segment.',
  concurrencySafe: true,
  cacheable: false,
  inputSchema: () =>
    z.object({
      entityType: z
        .string()
        .describe('Base entity the segment holds: Company or Person'),
      structure: z
        .preprocess(
          (v) => (typeof v === 'string' ? JSON.parse(v) : v),
          savedFilterStructureSchema,
        )
        .describe('The exact SavedFilterStructure from show_filter_preview'),
      name: z
        .string()
        .optional()
        .describe(
          'The segment name the user intends — enables the same-name collision check',
        ),
    }),

  async run(args) {
    const entityType = requireDataClassName(args.entityType)
    if (!entityType) {
      return { error: `Unknown entity type: ${args.entityType}` }
    }

    // Compare against the structure build_filter cached for this entity, not the
    // model-relayed arg (which drifts). Fall back to the arg only if none was built.
    const structure = getCachedFilter(entityType) ?? args.structure
    return findSegmentCollisions(entityType, structure, args.name)
  },
})
